import { createClient, SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '../types/database';

export type SupabaseConfig = {
  url: string;
  anonKey: string;
  /**
   * Async function that returns an access token for authenticated requests.
   * When using Auth0, this should return the Auth0 access token.
   * If not provided, requests will use the anon key only.
   */
  accessToken?: () => Promise<string>;
  options?: Parameters<typeof createClient>[2];
};

let instance: SupabaseClient<Database> | null = null;

/**
 * Check if we're running on localhost (development mode).
 */
export function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.')
  );
}

/**
 * Initialize the Supabase client with the given configuration.
 * This should be called once at app startup.
 *
 * @example
 * ```typescript
 * // In your app's entry point (e.g., main.tsx)
 * import { initSupabase } from '@nexus/supabase';
 *
 * initSupabase({
 *   url: import.meta.env.VITE_SUPABASE_URL,
 *   anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
 *   accessToken: async () => getAccessToken(),
 * });
 * ```
 */
export function initSupabase(config: SupabaseConfig): SupabaseClient<Database> {
  if (!config.url || !config.anonKey) {
    console.warn('[@nexus/supabase] Missing Supabase configuration. Client will not work correctly.');
  }

  instance = createClient<Database>(config.url || '', config.anonKey || '', {
    ...(config.accessToken && { accessToken: config.accessToken }),
    ...config.options,
  });

  return instance;
}

/**
 * Get the initialized Supabase client.
 * Throws an error if initSupabase() has not been called.
 *
 * @example
 * ```typescript
 * import { getSupabase } from '@nexus/supabase';
 *
 * const supabase = getSupabase();
 * const { data } = await supabase.from('feedback').select('*');
 * ```
 */
export function getSupabase(): SupabaseClient<Database> {
  if (!instance) {
    throw new Error('[@nexus/supabase] Supabase client not initialized. Call initSupabase() first.');
  }
  return instance;
}

/**
 * Check if the Supabase client has been initialized.
 */
export function isSupabaseInitialized(): boolean {
  return instance !== null;
}

// Re-export useful types from supabase-js
export type { SupabaseClient } from '@supabase/supabase-js';
export type { User, Session, AuthError } from '@supabase/supabase-js';
